import template from './jga-revocation-lookup-fields.html.twig';
import './jga-revocation-lookup-fields.scss';

const { Component } = Shopware;
const { Criteria } = Shopware.Data;

function generateId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '');
    }
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

Component.register('jga-revocation-lookup-fields', {
    template,

    inject: ['repositoryFactory'],

    props: {
        value: {
            type: Array,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: null,
        },
        helpText: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['update:value', 'change'],

    data() {
        return {
            editingItem: null,
            editingItemIndex: null,
            confirmDeleteIndex: null,
            knownFields: {},
        };
    },

    created() {
        this.refreshKnownFields();
    },

    computed: {
        customFieldRepository() {
            return this.repositoryFactory.create('custom_field');
        },

        items() {
            return Array.isArray(this.value) ? this.value : [];
        },

        // Criteria for the sw-entity-single-select inside the edit modal.
        // We intentionally do NOT restrict to "order"-relation only: many
        // connector plugins write to order.custom_fields via JSON without
        // formally binding the custom field set to the order entity (e.g.
        // lenz_platform_plenty_connector_order_plenty_id). The user is
        // expected to know which field carries the external identifier.
        // Already-configured fields are excluded so the dropdown cannot
        // produce duplicates (with the currently-edited item exempted so
        // editing it does not hide its own value).
        customFieldCriteria() {
            const criteria = new Criteria(1, 25);
            criteria.addAssociation('customFieldSet');
            criteria.addAssociation('customFieldSet.relations');
            criteria.addSorting(Criteria.sort('name', 'ASC'));

            const excludeIds = this.items
                .filter((_, idx) => idx !== this.editingItemIndex)
                .map((item) => item.customFieldId)
                .filter((id) => typeof id === 'string' && id !== '');

            if (excludeIds.length > 0) {
                criteria.addFilter(
                    Criteria.not('and', [Criteria.equalsAny('id', excludeIds)]),
                );
            }

            return criteria;
        },

        isDuplicate() {
            const id = this.editingItem?.customFieldId;
            if (typeof id !== 'string' || id === '') {
                return false;
            }
            return this.items.some((item, idx) =>
                idx !== this.editingItemIndex && item.customFieldId === id,
            );
        },
    },

    methods: {
        emitItems(items) {
            this.$emit('update:value', items);
            this.$emit('change', items);
        },

        async refreshKnownFields() {
            const ids = this.items
                .map((item) => item.customFieldId)
                .filter((id) => typeof id === 'string' && id !== '');
            if (ids.length === 0) {
                this.knownFields = {};
                return;
            }
            try {
                const criteria = new Criteria(1, 100);
                criteria.setIds(ids);
                const result = await this.customFieldRepository.search(criteria, Shopware.Context.api);
                const map = {};
                [...result].forEach((field) => {
                    map[field.id] = {
                        id: field.id,
                        name: field.name,
                        label: this.pickLabel(field),
                    };
                });
                this.knownFields = map;
            } catch (e) {
                this.knownFields = {};
            }
        },

        pickLabel(field) {
            const config = field?.config || {};
            const labels = config.label || {};
            const locale = Shopware.State.get('session')?.currentLocale || 'de-DE';
            if (typeof labels[locale] === 'string' && labels[locale] !== '') {
                return labels[locale];
            }
            if (typeof labels['de-DE'] === 'string' && labels['de-DE'] !== '') {
                return labels['de-DE'];
            }
            if (typeof labels['en-GB'] === 'string' && labels['en-GB'] !== '') {
                return labels['en-GB'];
            }
            const firstFilled = Object.values(labels).find((v) => typeof v === 'string' && v !== '');
            return firstFilled || field?.name || '';
        },

        addItem() {
            this.editingItem = {
                id: generateId(),
                customFieldId: null,
                customFieldName: null,
                active: true,
            };
            this.editingItemIndex = -1;
        },

        editItem(index) {
            const item = this.items[index];
            if (!item) {
                return;
            }
            this.editingItem = JSON.parse(JSON.stringify(item));
            this.editingItemIndex = index;
        },

        cancelEdit() {
            this.editingItem = null;
            this.editingItemIndex = null;
        },

        async onCustomFieldChange(customFieldId) {
            if (!this.editingItem) return;
            this.editingItem.customFieldId = customFieldId || null;
            if (!customFieldId) {
                this.editingItem.customFieldName = null;
                return;
            }
            try {
                const entity = await this.customFieldRepository.get(customFieldId, Shopware.Context.api);
                this.editingItem.customFieldName = entity?.name || null;
            } catch (e) {
                this.editingItem.customFieldName = null;
            }
        },

        async saveEdit() {
            if (!this.editingItem || !this.editingItem.customFieldId || !this.editingItem.customFieldName) {
                return;
            }
            if (this.isDuplicate) {
                return;
            }
            const next = [...this.items];
            if (this.editingItemIndex === -1) {
                next.push({ ...this.editingItem });
            } else if (this.editingItemIndex !== null) {
                next.splice(this.editingItemIndex, 1, { ...this.editingItem });
            }
            this.emitItems(next);
            this.cancelEdit();
            await this.$nextTick();
            this.refreshKnownFields();
            this.triggerPluginConfigSave();
        },

        triggerPluginConfigSave() {
            const candidates = Array.from(document.querySelectorAll(
                '.sw-extension-config button, .smart-bar__actions button, .sw-page__smart-bar button, .sw-system-config button'
            ));
            const saveBtn = candidates.find((btn) => {
                if (btn.disabled || btn.closest('.sw-modal')) return false;
                const label = (btn.innerText || '').trim().toLowerCase();
                return label === 'speichern' || label === 'save';
            });
            saveBtn?.click();
        },

        askDelete(index) {
            this.confirmDeleteIndex = index;
        },

        cancelDelete() {
            this.confirmDeleteIndex = null;
        },

        confirmDelete() {
            if (this.confirmDeleteIndex === null) {
                return;
            }
            const next = this.items.filter((_, idx) => idx !== this.confirmDeleteIndex);
            this.emitItems(next);
            this.confirmDeleteIndex = null;
        },

        setActive(index, value) {
            const next = this.items.map((item, idx) => (
                idx === index ? { ...item, active: this.coerceBool(value, !item.active) } : item
            ));
            this.emitItems(next);
        },

        coerceBool(value, fallback) {
            if (typeof value === 'boolean') return value;
            if (value && typeof value.target !== 'undefined' && typeof value.target.checked === 'boolean') {
                return value.target.checked;
            }
            return fallback;
        },

        labelForItem(item) {
            const known = this.knownFields[item?.customFieldId];
            if (known) {
                return known.label;
            }
            // Field was deleted in the meantime — show the stored name as a hint
            return this.$tc('jga-revocation.lookupFields.missingField');
        },

        technicalNameForItem(item) {
            return item?.customFieldName || '—';
        },

        isMissing(item) {
            return !this.knownFields[item?.customFieldId];
        },
    },
});
